import React, { useMemo, useEffect, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { dateTimeFormatter, getLocalDateFromString } from "../../utils/utils";
import { colors } from "../../utils/colors";
import sendImage from "../../assets/img/arrow-circle-up.png";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { useVirtualizer } from "@tanstack/react-virtual";

// ─── Styled Components ────────────────────────────────────────────────────────
const ChatInputWrapper = styled.div`
  height: 34px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border-radius: 50px;
  border: 1px solid #e5e5e5;
  background-color: #f5f5f5;
  outline: none;
`;

const ChatInput = styled.textarea`
  color: black;
  height: 20px;
  width: 100%;
  padding-left: 10px;
  padding-right: 45px;
  justify-content: flex-start;
  align-items: center;
  font-size: 16px;
  resize: none;
  border: none;
  background: none;
  outline: none;
  padding-top: 0px;
  padding-bottom: 0px;

  &:focus {
    outline: none;
    box-shadow: none;
  }
`;

const SendIcon = styled.div`
  position: absolute;
  right: 10px;
  bottom: 20px;
  width: 26px;
  height: 26px;
  background: transparent url(${sendImage}) no-repeat center center;
  background-size: 26px 26px;
  cursor: pointer;
`;

const ChatBubbleWrapper = styled.div`
  width: 100%;
`;

const UserChatBubble = styled.div`
  display: inline-block;
  margin-bottom: 20px;
  background-color: ${colors().lightAbyss};
  padding-top: 5px;
  padding-right: 10px;
  padding-bottom: 5px;
  padding-left: 10px;
  border-radius: 20px;
  align-self: flex-end;
  color: ${colors().darkestAbyss};
  font-size: 14px;
  text-align: left;
`;

// ─── ChatBubble (defined OUTSIDE parent to prevent remounting on each render) ─

const ChatBubble = ({
  type = "",
  message = "",
  message_id = null,
  relevance = 0,
  relevanceThreshold = 0,
  user = null,
  dateString = null,
  promoteContent = () => { },
}) => {
  if (!message) return null;

  if (!dateString) {
    dateString = dateTimeFormatter.format(new Date());
  }

  // Replace 8 leading spaces to prevent markdown treating it as a code block
  message = message.replace(/^ {8}/gm, "");

  return (
    <ChatBubbleWrapper
      style={{ textAlign: "left", flexDirection: "row", display: "flex" }}
    >
      <div
        style={{
          height: "30px",
          width: "30px",
          borderRadius: "50%",
          backgroundColor: "#f9d8b5",
          marginRight: "10px",
          marginTop: "10px",
          position: "relative",
          flexShrink: 0,
        }}
      >
        {user?.icon_background ? (
          <img
            src={require(`../../assets/live-event-icons/${user.icon_background}.png`)}
            alt="User Avatar"
            style={{ width: "100%", height: "100%", borderRadius: "50%" }}
          />
        ) : null}
        {user?.icon_name ? (
          <img
            src={require(`../../assets/live-event-icons/${user.icon_name}.png`)}
            alt="User Avatar"
            style={{
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              position: "absolute",
              top: "5px",
              left: "5px",
            }}
          />
        ) : null}
      </div>

      <UserChatBubble>
        <div
          style={{
            marginLeft: "5px",
            fontStyle: "italic",
            fontSize: "12px",
            color: "var(--chat-bubble-font-color)",
            marginBottom: "2px",
          }}
        >
          {dateString}
        </div>
        <div style={{ color: "var(--chat-bubble-font-color)", fontWeight: "700" }}>
          {user?.nickname?.replace(" null", "")}
        </div>
        <div
          style={{
            color: "var(--chat-bubble-font-color)",
            marginTop: "-8px",
            fontStyle: "italic",
          }}
        >
          {type}
          {(type === "comment" ||
            type === "statement" ||
            relevance < relevanceThreshold) && (
              <span
                onClick={() => promoteContent(message_id, relevance)}
                onMouseOver={(e) => (e.target.style.textDecoration = "underline")}
                onMouseOut={(e) => (e.target.style.textDecoration = "none")}
                style={{
                  color: "var(--chat-bubble-font-color)",
                  fontSize: "12px",
                  cursor: "pointer",
                }}
              >
                {" "}
              (promote)
              </span>
            )}
        </div>

        <ReactMarkdown
          rehypePlugins={[rehypeRaw]}
          components={{
            h1: ({ children }) => (
              <h1 style={{ color: "var(--chat-bubble-font-color)", fontSize: 32 }}>
                {children}
              </h1>
            ),
            b: ({ children }) => (
              <b style={{ color: "var(--chat-bubble-font-color)" }}>{children}</b>
            ),
            strong: ({ children }) => (
              <strong style={{ color: "var(--chat-bubble-font-color)" }}>
                {children}
              </strong>
            ),
            i: ({ children }) => (
              <i style={{ color: "var(--chat-bubble-font-color)" }}>{children}</i>
            ),
            em: ({ children }) => (
              <i style={{ color: "var(--chat-bubble-font-color)" }}>{children}</i>
            ),
            p: ({ children }) => (
              <p style={{ color: "var(--chat-bubble-font-color)" }}>{children}</p>
            ),
            a: ({ href, children }) => (
              <a
                href={href}
                style={{
                  color: "var(--chat-bubble-font-color)",
                  textDecoration: "underline",
                }}
              >
                {children}
              </a>
            ),
            code: ({ inline, children }) =>
              inline ? (
                <code
                  style={{ color: "var(--chat-bubble-font-color)" }}
                  className="bg-gray-100 px-1 rounded"
                >
                  {children}
                </code>
              ) : (
                <pre
                  style={{ color: "var(--chat-bubble-font-color)" }}
                  className="bg-gray-900 p-4 rounded"
                >
                  <code>{children}</code>
                </pre>
              ),
          }}
        >
          {message}
        </ReactMarkdown>
      </UserChatBubble>
    </ChatBubbleWrapper>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const LiveEventChatSession = ({
  display,
  messages,
  ws,
  event_id,
  user_id,
  height,
  ended = false,
  relevanceThreshold = 0,
  promoteContent = () => { },
}) => {
  const { token } = useSelector((state) => state.users);
  const [message, setMessage] = useState("");

  // Track whether the user is scrolled to (or near) the bottom
  const isAtBottom = useRef(true);

  // The scrollable container
  const chatWrapperRef = useRef(null);

  // Height of the scrollable container, kept in state so virtualizer can react
  const [containerHeight, setContainerHeight] = useState(0);

  // ── Observe container height changes (window resize, panel resize, etc.) ────
  useEffect(() => {
    const el = chatWrapperRef.current;
    if (!el) return;

    const ro = new ResizeObserver(([entry]) => {
      setContainerHeight(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Virtualizer ──────────────────────────────────────────────────────────────
  const sortedMessages = useMemo(() => [...(messages ?? [])].sort(
    (a, b) => new Date(a.created_at) - new Date(b.created_at)
  ), [messages]);

  // Compute BEFORE useVirtualizer
  const estimatedTotalSize = sortedMessages.reduce((sum, msg) => {
    const text = msg?.message ?? "";
    return sum + text.split("\n").length * 20 + Math.ceil(text.length / 50) * 20 + 60;
  }, 0);

  const paddingStart = Math.max(0, containerHeight - estimatedTotalSize);


  const virtualizer = useVirtualizer({
    count: sortedMessages.length,
    getScrollElement: () => chatWrapperRef.current,
    overscan: 10,

    // Rough height estimate — measureElement will correct this per-item
    estimateSize: (index) => {
      const text = sortedMessages[index]?.message ?? "";
      return (
        text.split("\n").length * 16 + Math.ceil(text.length / 50) * 16 + 60
      );
    },

    // Push content down so short lists sit at the bottom of the container
    // instead of rendering at the top with empty space below.
    paddingStart: paddingStart,
    getItemKey: (index) => sortedMessages[index]?.id || index,
  });

  // ── Scroll to bottom when new messages arrive (only if already at bottom) ───
  useEffect(() => {
    if (!sortedMessages.length) return;
    if (!isAtBottom.current) return;

    // rAF ensures the virtualizer has finished painting before we scroll
    requestAnimationFrame(() => {
      virtualizer.scrollToIndex(sortedMessages.length - 1, {
        align: "end",
        behavior: "smooth",
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedMessages.length]);

  // ── Track whether the user is near the bottom ────────────────────────────────
  const handleScroll = useCallback(() => {
    const el = chatWrapperRef.current;
    if (!el) return;
    // Within 80px of the bottom → treat as "at bottom"
    isAtBottom.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  // ── Send message ─────────────────────────────────────────────────────────────
  const sendMessage = () => {
    if (!ws.current || !message.trim()) return;
    ws.current.emit("event-message", {
      token,
      event_id,
      message,
      user_id,
    });
    setMessage("");
    // Force scroll-to-bottom after sending your own message
    isAtBottom.current = true;
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (!e.shiftKey) {
        e.preventDefault();
        sendMessage();
      } else {
        setMessage((prev) => prev + "\n");
      }
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        width: "100%",
        height,
        position: "relative",
        display: display ? "block" : "none",
      }}
    >
      <div
        className="border-0 rounded-lg shadow-lg relative flex flex-col bg-white outline-none focus:outline-none"
        style={{ width: "100%", height: "100%", backgroundColor: "var(--main-bg)" }}
      >
        {/* Message list */}
        <div style={{ height: "calc(100% - 50px)" }}>
          <div
            ref={chatWrapperRef}
            onScroll={handleScroll}
            className="my-4 text-blueGray-500 text-lg leading-relaxed overflow-auto"
            style={{ height: "100%" }}   // ← removed "flex" here
          >
            {/* Sized content area for the virtualizer */}
            <div
              style={{
                height: virtualizer.getTotalSize(),
                position: "relative",
              }}
            >
              {virtualizer.getVirtualItems().map((virtualRow) => {
                const msg = sortedMessages[virtualRow.index];
                return (
                  <div
                    key={msg.id}
                    data-index={virtualRow.index}
                    ref={virtualizer.measureElement} // measures real height after render
                    style={{
                      position: "absolute",
                      top: virtualRow.start,
                      width: "100%",
                      // ⚠️ no fixed height — let content define it
                    }}
                  >
                    <ChatBubble
                      type={msg.message_type}
                      message_id={msg.public_id}
                      message={msg.message}
                      user={msg.user}
                      relevance={msg.relevance}
                      relevanceThreshold={relevanceThreshold}
                      promoteContent={promoteContent}
                      dateString={
                        msg.created_at
                          ? dateTimeFormatter.format(
                            getLocalDateFromString(msg.created_at)
                          )
                          : null
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Input bar */}
        <div style={{ height: "40px", zIndex: 2000 }}>
          <ChatInputWrapper>
            <ChatInput
              value={message}
              onKeyPress={handleKeyPress}
              onChange={(e) => setMessage(e.target.value)}
              style={{ height: "100%" }}
            />
            <SendIcon
              style={{ display: message.length > 0 ? "block" : "none" }}
              onClick={sendMessage}
            />
          </ChatInputWrapper>
        </div>
      </div>
    </div>
  );
};

export default LiveEventChatSession;
